import React, { useState, useEffect } from 'react'
import { useApi } from '../../contexts/ApiContext'
import { useAuth } from '../../contexts/AuthContext'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { trackPageView, trackHospitalSearch, trackHospitalView, openChatWithContext } from '../../utils/eventTracking'
import { 
  Building2, 
  Search, 
  Filter, 
  MapPin, 
  Phone, 
  Mail, 
  Star, 
  Users, 
  Clock,
  Shield,
  Eye,
  ChevronDown,
  X,
  FileText,
  Stethoscope,
  ArrowRight,
  MessageCircle
} from 'lucide-react'

const CustomerHospitals = () => {
  const { getHospitals } = useApi()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  
  const [hospitals, setHospitals] = useState([])
  const [filteredHospitals, setFilteredHospitals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // User location state
  const [userCity, setUserCity] = useState('')
  const [cityHospitals, setCityHospitals] = useState([])
  const [cityHospitalCount, setCityHospitalCount] = useState(0)
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCity, setSelectedCity] = useState('')
  const [selectedState, setSelectedState] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedSort, setSelectedSort] = useState('rating_desc')
  const [showFilters, setShowFilters] = useState(false)
  
  // Unique values for filters
  const [cities, setCities] = useState([])
  const [states, setStates] = useState([])
  const [types, setTypes] = useState([])
  const [statuses, setStatuses] = useState([])
  
  // Modal state
  const [selectedHospital, setSelectedHospital] = useState(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    loadHospitals()
    getUserCity()
    
    // Check for city parameter in URL
    const cityFromUrl = searchParams.get('city')
    if (cityFromUrl) {
      console.log('City from URL:', cityFromUrl)
      setSelectedCity(cityFromUrl)
      setShowFilters(true) // Show filters when coming from dashboard
      // Also set userCity to the URL city for consistency
      setUserCity(cityFromUrl)
    }
  }, [])

  useEffect(() => {
    if (userCity) {
      loadHospitals()
    }
  }, [userCity])

  useEffect(() => {
    filterHospitals()
  }, [hospitals, searchTerm, selectedCity, selectedState, selectedType, selectedStatus, selectedSort])

  // City mapping function to handle variations
  const mapCityName = (sessionCity) => {
    const cityMappings = {
      'Bengaluru': 'Bangalore',
      'Bangalore': 'Bangalore',
      'Mumbai': 'Mumbai',
      'Delhi': 'Delhi',
      'New Delhi': 'Delhi',
      'Kolkata': 'Kolkata',
      'Calcutta': 'Kolkata',
      'Chennai': 'Chennai',
      'Madras': 'Chennai',
      'Hyderabad': 'Hyderabad',
      'Pune': 'Pune',
      'Ahmedabad': 'Ahmedabad',
      'Jaipur': 'Jaipur',
      'Lucknow': 'Lucknow',
      'Kanpur': 'Kanpur',
      'Nagpur': 'Nagpur',
      'Indore': 'Indore',
      'Thane': 'Thane',
      'Bhopal': 'Bhopal',
      'Visakhapatnam': 'Visakhapatnam',
      'Vizag': 'Visakhapatnam',
      'Pimpri-Chinchwad': 'Pune',
      'Patna': 'Patna',
      'Vadodara': 'Vadodara',
      'Ludhiana': 'Ludhiana',
      'Agra': 'Agra',
      'Nashik': 'Nashik',
      'Faridabad': 'Faridabad',
      'Meerut': 'Meerut',
      'Rajkot': 'Rajkot',
      'Kalyan-Dombivali': 'Mumbai',
      'Vasai-Virar': 'Mumbai',
      'Varanasi': 'Varanasi',
      'Srinagar': 'Srinagar',
      'Aurangabad': 'Aurangabad',
      'Navi Mumbai': 'Mumbai',
      'Solapur': 'Solapur',
      'Vijayawada': 'Vijayawada',
      'Kolhapur': 'Kolhapur',
      'Amritsar': 'Amritsar',
      'Noida': 'Delhi',
      'Ghaziabad': 'Delhi',
      'Gurgaon': 'Gurgaon',
      'Gurugram': 'Gurgaon'
    }
    
    return cityMappings[sessionCity] || sessionCity
  }

  // Function to get user's city from session data
  const getUserCity = async () => {
    try {
      if (user && user.id) {
        const { data, error } = await supabase
          .from('sessions')
          .select('location_city')
          .eq('user_id', user.id)
          .eq('session_type', 'login')
          .order('login_time', { ascending: false })
          .limit(1)
          .single()

        if (data && data.location_city) {
          const mappedCity = mapCityName(data.location_city)
          setUserCity(mappedCity)
        } else {
          // Fallback to default city
          setUserCity('Mumbai')
          console.log('Using default city: Mumbai')
        }
      } else {
        setUserCity('Mumbai')
        console.log('No user found, using default city: Mumbai')
      }
    } catch (error) {
      console.error('Error getting user city:', error)
      setUserCity('Mumbai')
    }
  }

  const loadHospitals = async () => {
    try {
      setLoading(true)
      const data = await getHospitals()
      setHospitals(data || [])
      
      // Filter hospitals by user's city
      if (userCity && data) {
        // Debug: Log all unique cities in hospital data
        const uniqueHospitalCities = [...new Set(data.map(h => h.city).filter(Boolean))]
        console.log('Available hospital cities:', uniqueHospitalCities)
        console.log('Looking for user city:', userCity)
        console.log('Total hospitals loaded:', data.length)
        
        const cityFilteredHospitals = data.filter(hospital => {
          if (!hospital.city) return false
          // Use case-insensitive comparison and handle city variations
          const hospitalCity = hospital.city.toLowerCase().trim()
          const mappedUserCity = userCity.toLowerCase().trim()
          
          // Also check for partial matches and common variations
          const isExactMatch = hospitalCity === mappedUserCity
          const isPartialMatch = hospitalCity.includes(mappedUserCity) || mappedUserCity.includes(hospitalCity)
          
          if (isExactMatch || isPartialMatch) {
            console.log(`✅ Matched hospital city: ${hospital.city} with user city: ${userCity}`)
            return true
          }
          return false
        })
        setCityHospitals(cityFilteredHospitals)
        setCityHospitalCount(cityFilteredHospitals.length)
        console.log(`Found ${cityFilteredHospitals.length} hospitals in ${userCity}`)
        
        // If no hospitals found, show some debug info
        if (cityFilteredHospitals.length === 0) {
          console.log('❌ No hospitals found for city:', userCity)
          console.log('Available cities:', uniqueHospitalCities)
          console.log('Trying to find Bangalore variations...')
          const bangaloreVariations = data.filter(h => 
            h.city && (
              h.city.toLowerCase().includes('bangalore') || 
              h.city.toLowerCase().includes('bengaluru') ||
              h.city.toLowerCase().includes('bangaluru')
            )
          )
          console.log('Bangalore variations found:', bangaloreVariations.length)
          if (bangaloreVariations.length > 0) {
            console.log('Bangalore hospitals:', bangaloreVariations.map(h => h.city))
          }
        }
      }
      
      // Extract unique values for filters
      const uniqueCities = [...new Set(data?.map(h => h.city).filter(Boolean))].sort()
      const uniqueStates = [...new Set(data?.map(h => h.state).filter(Boolean))].sort()
      const uniqueTypes = [...new Set(data?.map(h => h.hospital_type).filter(Boolean))].sort()
      const uniqueStatuses = [...new Set(data?.map(h => h.network_status).filter(Boolean))].sort()
      
      setCities(uniqueCities)
      setStates(uniqueStates)
      setTypes(uniqueTypes)
      setStatuses(uniqueStatuses)
    } catch (err) {
      console.error('Error loading hospitals:', err)
      setError('Failed to load hospitals')
    } finally {
      setLoading(false)
    }
  }

  const filterHospitals = () => {
    let filtered = hospitals

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(hospital => 
        hospital.hospital_name?.toLowerCase().includes(term) ||
        hospital.hospital_code?.toLowerCase().includes(term) ||
        hospital.city?.toLowerCase().includes(term) ||
        hospital.state?.toLowerCase().includes(term) ||
        (hospital.specialties && typeof hospital.specialties === 'string' && hospital.specialties.toLowerCase().includes(term))
      )
    }

    // City filter
    if (selectedCity) {
      const cityFiltered = filtered.filter(hospital => {
        if (!hospital.city) return false
        // Use case-insensitive comparison and handle city variations
        const hospitalCity = hospital.city.toLowerCase().trim()
        const selectedCityLower = selectedCity.toLowerCase().trim()
        
        // Also check for partial matches and common variations
        const isExactMatch = hospitalCity === selectedCityLower
        const isPartialMatch = hospitalCity.includes(selectedCityLower) || selectedCityLower.includes(hospitalCity)
        
        return isExactMatch || isPartialMatch
      })
      
      // If city filter results in zero hospitals, show all hospitals with a warning
      if (cityFiltered.length === 0) {
        // Don't apply city filter, keep all hospitals
      } else {
        filtered = cityFiltered
      }
    }

    // State filter
    if (selectedState) {
      filtered = filtered.filter(hospital => hospital.state === selectedState)
    }

    // Type filter
    if (selectedType) {
      filtered = filtered.filter(hospital => hospital.hospital_type === selectedType)
    }

    // Status filter
    if (selectedStatus) {
      filtered = filtered.filter(hospital => hospital.network_status === selectedStatus)
    }

    // Sort the results
    filtered = filtered.sort((a, b) => {
      switch (selectedSort) {
        case 'rating_desc':
          const ratingA = parseFloat(a.quality_rating) || 0
          const ratingB = parseFloat(b.quality_rating) || 0
          return ratingB - ratingA
        case 'rating_asc':
          const ratingA_asc = parseFloat(a.quality_rating) || 0
          const ratingB_asc = parseFloat(b.quality_rating) || 0
          return ratingA_asc - ratingB_asc
        case 'name_asc':
          return (a.hospital_name || '').localeCompare(b.hospital_name || '')
        case 'name_desc':
          return (b.hospital_name || '').localeCompare(a.hospital_name || '')
        case 'beds_desc':
          const bedsA = parseInt(a.bed_count) || 0
          const bedsB = parseInt(b.bed_count) || 0
          return bedsB - bedsA
        case 'beds_asc':
          const bedsA_asc = parseInt(a.bed_count) || 0
          const bedsB_asc = parseInt(b.bed_count) || 0
          return bedsA_asc - bedsB_asc
        default:
          return 0
      }
    })

    setFilteredHospitals(filtered)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedCity('')
    setSelectedState('')
    setSelectedType('')
    setSelectedStatus('')
    setSelectedSort('rating_desc')
  }

  const handleViewAll = () => {
    // Apply city filter and show all hospitals
    const cityFromUrl = searchParams.get('city')
    const cityToUse = cityFromUrl || userCity
    
    console.log('View All clicked - using city:', cityToUse)
    setSelectedCity(cityToUse)
    setSearchTerm('')
    setSelectedState('')
    setSelectedType('')
    setSelectedStatus('')
    setShowFilters(true) // Show filters when applying city filter
  }

  const handleViewDetails = (hospital) => {
    setSelectedHospital(hospital)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedHospital(null)
  }

  const formatPhone = (phone) => {
    if (!phone || typeof phone !== 'string') return 'N/A'
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800'
      case 'INACTIVE': return 'bg-red-100 text-red-800'
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'IN_NETWORK': return 'bg-green-100 text-green-800'
      default: return 'bg-green-100 text-green-800'
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'GOVERNMENT': return 'bg-blue-100 text-blue-800'
      case 'PRIVATE': return 'bg-purple-100 text-purple-800'
      case 'SEMI_PRIVATE': return 'bg-orange-100 text-orange-800'
      case 'MULTI_SPECIALTY': return 'bg-purple-100 text-purple-800'
      case 'MULTI SPECIALTY': return 'bg-purple-100 text-purple-800'
      default: return 'bg-purple-100 text-purple-800'
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Network Hospitals</h1>
          <p className="text-xs sm:text-sm text-gray-600">Find hospitals in our network</p>
        </div>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading hospitals...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Network Hospitals</h1>
          <p className="text-xs sm:text-sm text-gray-600">Find hospitals in our network</p>
        </div>
        <div className="text-center py-12">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Network Hospitals</h1>
        <p className="text-xs sm:text-sm text-gray-600">
          Find hospitals in our network ({filteredHospitals.length} hospitals found)
          {selectedCity && (
            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              Filtered by: {selectedCity}
            </span>
          )}
        </p>
      </div>

      {/* City-based Hospital Count and Cards */}
      {userCity && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div className="flex-1">
              <h2 className="text-base sm:text-lg font-semibold text-blue-900 flex items-center">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Hospitals in {userCity}
              </h2>
              <p className="text-xs sm:text-sm text-blue-700">
                {cityHospitalCount > 0 ? `${cityHospitalCount} network hospitals available in your city` : `No hospitals found in ${userCity} - showing all hospitals`}
              </p>
            </div>
            <button
              onClick={handleViewAll}
              className="flex items-center gap-1 sm:gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm whitespace-nowrap self-start"
            >
              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>View All</span>
            </button>
          </div>
          
          {/* City Hospital Cards */}
          {cityHospitalCount > 0 ? (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {cityHospitals.slice(0, 4).map((hospital) => (
                  <div
                    key={hospital.hospital_id}
                    className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    {/* Header */}
                    <div className="mb-3">
                      <div className="flex items-start space-x-2 mb-3">
                        <div className="bg-blue-100 p-1.5 rounded-lg">
                          <Building2 className="h-3 w-3 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 text-sm leading-tight flex-1">{hospital.hospital_name || 'N/A'}</h3>
                      </div>
                      
                      {/* Labels Section */}
                      <div className="space-y-1 mb-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Code:</span>
                          <span className="font-medium text-gray-900">{hospital.hospital_code || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Network:</span>
                          <span className={`px-1 py-0.5 text-xs rounded-full font-medium ${getStatusColor(hospital.network_status)}`} style={{ fontSize: '10px' }}>
                            {hospital.network_status || 'IN_NETWORK'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Type:</span>
                          <span className={`px-1 py-0.5 text-xs rounded-full font-medium ${getTypeColor(hospital.hospital_type)}`} style={{ fontSize: '10px' }}>
                            {hospital.hospital_type || 'MULTI SPECIALTY'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <MapPin className="h-3 w-3" />
                        <span className="text-xs">
                          {hospital.city || 'N/A'}, {hospital.state || 'N/A'}
                        </span>
                      </div>
                      
                      {hospital.contact_phone && (
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Phone className="h-3 w-3" />
                          <span className="text-xs">{formatPhone(hospital.contact_phone)}</span>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="space-y-1 mb-2">
                      {hospital.bed_count && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Beds</span>
                          <span className="font-medium text-xs">{hospital.bed_count}</span>
                        </div>
                      )}
                      
                      {hospital.quality_rating && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Rating</span>
                          <div className="flex items-center space-x-1">
                            <Star className="h-3 w-3 text-yellow-400 fill-current" />
                            <span className="font-medium text-xs">{hospital.quality_rating || 'N/A'}/5</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleViewDetails(hospital)}
                        className="w-full flex items-center justify-center space-x-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 py-1 px-2 rounded-lg transition-colors whitespace-nowrap"
                      >
                        <Eye className="h-3 w-3 flex-shrink-0" />
                        <span className="whitespace-nowrap">View Details</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {cityHospitalCount > 4 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleViewAll}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View {cityHospitalCount - 4} more hospitals in {userCity} →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-4">
                <Building2 className="h-12 w-12 mx-auto mb-2" />
                <p className="text-sm">No hospitals found in {userCity}</p>
                <p className="text-xs text-gray-400 mt-1">Check the console for debug information</p>
              </div>
              <button
                onClick={handleViewAll}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View all hospitals →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search hospitals by name, code, city, or specialties..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            <ChevronDown className={`h-3 w-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* City Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Cities</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* State Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All States</option>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Hospital Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Types</option>
                  {types.map(type => (
                    <option key={type} value={type}>{type.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Network Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Status</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {/* Sort Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={selectedSort}
                  onChange={(e) => setSelectedSort(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="rating_desc">Rating (High to Low)</option>
                  <option value="rating_asc">Rating (Low to High)</option>
                  <option value="name_asc">Name (A to Z)</option>
                  <option value="name_desc">Name (Z to A)</option>
                  <option value="beds_desc">Beds (High to Low)</option>
                  <option value="beds_asc">Beds (Low to High)</option>
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="flex items-center space-x-2 px-3 py-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
              >
                <X className="h-3 w-3" />
                <span>Clear All Filters</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hospitals Grid */}
      {filteredHospitals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredHospitals.map((hospital) => (
            <div
              key={hospital.hospital_id}
              className="bg-white border-2 border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200"
            >
              {/* Header */}
              <div className="mb-3">
                <div className="flex items-start space-x-2 mb-4">
                  <div className="bg-blue-100 p-1.5 rounded-lg">
                    <Building2 className="h-3 w-3 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-xs leading-tight flex-1">{hospital.hospital_name || 'N/A'}</h3>
                </div>
                
                {/* Labels Section */}
                <div className="space-y-1 mb-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Code:</span>
                    <span className="font-medium text-gray-900">{hospital.hospital_code || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Network:</span>
                    <span className={`px-1 py-0.5 text-xs rounded-full font-medium ${getStatusColor(hospital.network_status)}`} style={{ fontSize: '10px' }}>
                      {hospital.network_status || 'IN_NETWORK'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Type:</span>
                    <span className={`px-1 py-0.5 text-xs rounded-full font-medium ${getTypeColor(hospital.hospital_type)}`} style={{ fontSize: '10px' }}>
                      {hospital.hospital_type || 'MULTI SPECIALTY'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2 mb-3">
                <div className="flex items-center space-x-1 text-gray-600">
                  <MapPin className="h-3 w-3" />
                  <span className="text-xs">
                    {hospital.city || 'N/A'}, {hospital.state || 'N/A'}
                  </span>
                </div>
                
                {hospital.contact_phone && (
                  <div className="flex items-center space-x-1 text-gray-600">
                    <Phone className="h-3 w-3" />
                    <span className="text-xs">{formatPhone(hospital.contact_phone)}</span>
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="space-y-1 mb-2">
                {hospital.bed_count && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Beds</span>
                    <span className="font-medium text-xs">{hospital.bed_count}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Quality Rating</span>
                  <div className="flex items-center space-x-1">
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                    <span className="font-medium text-xs">{hospital.quality_rating || 'N/A'}/5</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="pt-3 border-t border-gray-100">
                <button
                  onClick={() => handleViewDetails(hospital)}
                  className="w-full flex items-center justify-center space-x-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 py-1 px-2 rounded-lg transition-colors whitespace-nowrap"
                >
                  <Eye className="h-3 w-3 flex-shrink-0" />
                  <span className="whitespace-nowrap">View Details</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center">
          <Building2 className="h-8 w-8 text-gray-400 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">No Hospitals Found</h3>
          <p className="text-xs text-gray-500">Try adjusting your search criteria or filters.</p>
        </div>
      )}

      {/* Hospital Details Modal */}
      {showModal && selectedHospital && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex-1">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 leading-tight">{selectedHospital.hospital_name}</h2>
                </div>
                
                {/* Labels Section */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Code:</span>
                    <span className="font-medium text-gray-900">{selectedHospital.hospital_code}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Network:</span>
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(selectedHospital.network_status)}`} style={{ fontSize: '11px' }}>
                      {selectedHospital.network_status || 'IN_NETWORK'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Type:</span>
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${getTypeColor(selectedHospital.hospital_type)}`} style={{ fontSize: '11px' }}>
                      {selectedHospital.hospital_type || 'MULTI SPECIALTY'}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Quick Status Overview */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="text-lg font-semibold text-gray-900">{selectedHospital.quality_rating || 'N/A'}/5</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Quality Rating</p>
                  </div>
                  {selectedHospital.bed_count && (
                    <div className="text-center">
                      <span className="text-lg font-semibold text-gray-900">{selectedHospital.bed_count}</span>
                      <p className="text-xs text-gray-600 mt-1">Beds Available</p>
                    </div>
                  )}
                  {selectedHospital.emergency_24x7 && (
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <Clock className="h-4 w-4 text-red-500" />
                        <span className="text-sm font-semibold text-gray-900">24x7</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Emergency</p>
                    </div>
                  )}
                  {selectedHospital.cashless_available && (
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <Shield className="h-4 w-4 text-green-500" />
                        <span className="text-sm font-semibold text-gray-900">Cashless</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Location & Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                  Location & Contact
                </h3>
                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      {selectedHospital.address_line1 && (
                        <p className="text-sm font-medium text-gray-900">{selectedHospital.address_line1}</p>
                      )}
                      {selectedHospital.address_line2 && (
                        <p className="text-sm font-medium text-gray-900">{selectedHospital.address_line2}</p>
                      )}
                      <div className="space-y-1">
                        {selectedHospital.city && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">City:</span> {selectedHospital.city}
                          </p>
                        )}
                        {selectedHospital.state && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">State:</span> {selectedHospital.state}
                          </p>
                        )}
                        {selectedHospital.postal_code && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Postal Code:</span> {selectedHospital.postal_code}
                          </p>
                        )}
                        {selectedHospital.country && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Country:</span> {selectedHospital.country}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {selectedHospital.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{formatPhone(selectedHospital.phone)}</span>
                        <p className="text-xs text-gray-500">Main Number</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedHospital.emergency_contact && (
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{formatPhone(selectedHospital.emergency_contact)}</span>
                        <p className="text-xs text-gray-500">Emergency Contact</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedHospital.email && (
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{selectedHospital.email}</span>
                        <p className="text-xs text-gray-500">Email Address</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Services & Facilities */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Shield className="h-5 w-5 text-green-600 mr-2" />
                  Services & Facilities
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Hospital Services</h4>
                    <div className="space-y-2">
                      {selectedHospital.insurance_accepted !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Insurance Accepted</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedHospital.insurance_accepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {selectedHospital.insurance_accepted ? 'Yes' : 'No'}
                          </span>
                        </div>
                      )}
                      {selectedHospital.emergency_services !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Emergency Services</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedHospital.emergency_services ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {selectedHospital.emergency_services ? 'Available' : 'Not Available'}
                          </span>
                        </div>
                      )}
                      {selectedHospital.bed_count && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Total Beds</span>
                          <span className="text-sm font-medium text-gray-900">{selectedHospital.bed_count}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Additional Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Verification Status</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedHospital.verification_status === 'VERIFIED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {selectedHospital.verification_status}
                        </span>
                      </div>
                      {selectedHospital.emergency_24x7 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">24x7 Emergency</span>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Available
                          </span>
                        </div>
                      )}
                      {selectedHospital.cashless_available && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Cashless Treatment</span>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Available
                          </span>
                        </div>
                      )}
                      {selectedHospital.pre_approval_required !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Pre-approval Required</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedHospital.pre_approval_required ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {selectedHospital.pre_approval_required ? 'Yes' : 'No'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Medical Specialties */}
              {selectedHospital.specialties && typeof selectedHospital.specialties === 'string' && selectedHospital.specialties.trim() && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Stethoscope className="h-5 w-5 text-purple-600 mr-2" />
                    Medical Specialties
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex flex-wrap gap-2">
                      {selectedHospital.specialties.split(',').map((specialty, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full font-medium"
                        >
                          {specialty.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Information */}
              {(selectedHospital.description || selectedHospital.website) && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FileText className="h-5 w-5 text-gray-600 mr-2" />
                    Additional Information
                  </h3>
                  
                  {selectedHospital.description && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">About This Hospital</h4>
                      <p className="text-sm text-gray-700 leading-relaxed">{selectedHospital.description}</p>
                    </div>
                  )}
                  
                  {selectedHospital.website && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Website</h4>
                      <a 
                        href={selectedHospital.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm underline flex items-center"
                      >
                        <span>{selectedHospital.website}</span>
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  const hospitalName = selectedHospital.hospital_name || 'N/A'
                  const hospitalCode = selectedHospital.hospital_code || 'N/A'
                  const city = selectedHospital.city || 'N/A'
                  const hospitalType = selectedHospital.hospital_type || 'N/A'
                  const networkStatus = selectedHospital.network_status || 'IN_NETWORK'
                  
                  openChatWithContext(
                    `I need information about ${hospitalName} (Code: ${hospitalCode}) in ${city}. It's a ${hospitalType} hospital with ${networkStatus} status. Can you help me with details about this hospital?`,
                    'hospital_details_modal'
                  )
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
              >
                <MessageCircle className="h-5 w-5" />
                <span className="font-medium">Chat About Hospital</span>
              </button>
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerHospitals